import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export class AuthManager {
  constructor() {
    this.user = null;
    this.session = null;
    this.gameState = null;
    
    // Listen for auth changes
    supabase.auth.onAuthStateChange((event, session) => {
      this.session = session;
      this.user = session?.user || null;
      
      if (event === 'SIGNED_IN') {
        this.loadGameState();
      } else if (event === 'SIGNED_OUT') {
        this.gameState = null;
      }
    });
  }
  
  async init() {
    const { data: { session } } = await supabase.auth.getSession();
    this.session = session;
    this.user = session?.user || null;
    
    if (this.user) {
      await this.loadGameState();
    }
    
    return this.user;
  }
  
  // Anonymous sign in (for quick play)
  async signInAnonymously() {
    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) throw error;
    return data.user;
  }
  
  // Email sign up
  async signUp(email, password, username) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username }
      }
    });
    if (error) throw error;
    return data.user;
  }
  
  // Email sign in
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data.user;
  }
  
  // OAuth sign in (GitHub, Google, etc.)
  async signInWithOAuth(provider) {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) throw error;
    return data;
  }
  
  async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }
  
  // Load game state from Supabase
  async loadGameState() {
    if (!this.user) return null;
    
    const { data, error } = await supabase
      .from('game_states')
      .select('*')
      .eq('player_id', this.user.id)
      .single();
    
    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows
      console.error('Error loading game state:', error);
      return null;
    }
    
    this.gameState = data || this.getDefaultGameState();
    return this.gameState;
  }
  
  // Save game state to Supabase
  async saveGameState(state) {
    if (!this.user) return;
    
    const { error } = await supabase
      .from('game_states')
      .upsert({
        player_id: this.user.id,
        ...state,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'player_id'
      });
    
    if (error) {
      console.error('Error saving game state:', error);
      throw error;
    }
    
    this.gameState = state;
  }
  
  getDefaultGameState() {
    return {
      current_day: 1,
      current_year: 2045,
      time_of_day: 'morning',
      act: 1,
      money: 0,
      tips_today: 0,
      total_coffees_made: 0,
      perfect_coffees: 0,
      story_flags: {},
      knows_truth: false
    };
  }
  
  // Save conversation to database
  async saveConversation(npcId, messages, gameDay, gameYear) {
    if (!this.user) return;
    
    const { data, error } = await supabase
      .from('conversations')
      .insert({
        player_id: this.user.id,
        npc_id: npcId,
        messages: messages,
        game_day: gameDay,
        game_year: gameYear
      })
      .select()
      .single();
    
    if (error) {
      console.error('Error saving conversation:', error);
      throw error;
    }
    
    return data;
  }
  
  // Load recent conversations with an NPC (for context)
  async getRecentConversations(npcId, limit = 5) {
    if (!this.user) return [];
    
    const { data, error } = await supabase
      .from('conversations')
      .select('*')
      .eq('player_id', this.user.id)
      .eq('npc_id', npcId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) {
      console.error('Error loading conversations:', error);
      return [];
    }
    
    return data || [];
  }
  
  // Update relationship with NPC
  async updateRelationship(npcId, changes) {
    if (!this.user) return;
    
    // Get current relationship
    const { data: current } = await supabase
      .from('relationships')
      .select('*')
      .eq('player_id', this.user.id)
      .eq('npc_id', npcId)
      .single();
    
    const updates = {
      player_id: this.user.id,
      npc_id: npcId,
      ...(current || {}),
      ...changes,
      updated_at: new Date().toISOString()
    };
    
    // Clamp values
    if (updates.affection !== undefined) {
      updates.affection = Math.max(-100, Math.min(100, updates.affection));
    }
    if (updates.trust !== undefined) {
      updates.trust = Math.max(-100, Math.min(100, updates.trust));
    }
    
    const { error } = await supabase
      .from('relationships')
      .upsert(updates, {
        onConflict: 'player_id,npc_id'
      });
    
    if (error) {
      console.error('Error updating relationship:', error);
      throw error;
    }
  }
  
  // Get relationship with NPC
  async getRelationship(npcId) {
    if (!this.user) return null;
    
    const { data, error } = await supabase
      .from('relationships')
      .select('*')
      .eq('player_id', this.user.id)
      .eq('npc_id', npcId)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      console.error('Error loading relationship:', error);
    }
    
    return data || { affection: 0, trust: 0, times_talked: 0 };
  }
  
  // Get all NPCs
  async getNPCs() {
    const { data, error } = await supabase
      .from('npcs')
      .select('*');
    
    if (error) {
      console.error('Error loading NPCs:', error);
      return [];
    }
    
    return data || [];
  }
}

// Singleton instance
export const authManager = new AuthManager();
